import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { DocumentStatusBadge } from './DocumentStatusBadge'
import {
  documentTitle,
  entityDisplay,
  formatDate,
  groupDisplay,
} from './documentUtils'
import { unavailableReason } from '@/features/scope'

interface Props {
  doc: CatalogDocumentDTO
  isActiveScope: boolean
  canUseAsScope: boolean
  onUseAsScope: () => void
  /** Step 7.9C — opens the delete confirmation modal for this document. */
  onRequestDelete: () => void
  /**
   * True while the delete request for this card is in-flight. Keeps the trash
   * button disabled and visually busy so a double-click can't fire two DELETEs.
   */
  isDeleting?: boolean
}

/**
 * Glass card representing a single indexed document. The "Usar no chat" CTA
 * is disabled when the document is not yet retrievable (no chunks / failed
 * status) so the user never selects something the backend would reject.
 * When the document is unusable, an inline warning explains *why* — chunks=0,
 * failed status, etc. — so non-technical users aren't stuck guessing.
 *
 * Step 7.9C: a subtle trash icon lives in the card header. It opens a
 * confirmation modal owned by the parent page so the actual DELETE call,
 * toast, and scope cleanup stay co-located there.
 */
export function DocumentCard({
  doc,
  isActiveScope,
  canUseAsScope,
  onUseAsScope,
  onRequestDelete,
  isDeleting = false,
}: Props) {
  const title = documentTitle(doc)
  const subtitleParts: string[] = []
  const entityLabel = entityDisplay(doc)
  if (doc.entity_type && entityLabel) {
    subtitleParts.push(`${doc.entity_type} / ${entityLabel}`)
  }
  const groupLabel = groupDisplay(doc)
  if (doc.group_type && groupLabel) {
    subtitleParts.push(`${doc.group_type} / ${groupLabel}`)
  }

  const indexedAtFmt = formatDate(doc.indexed_at)
  const createdAtFmt = formatDate(doc.created_at)
  const showCreatedRow = createdAtFmt !== '—' && createdAtFmt !== indexedAtFmt

  const warning = !canUseAsScope ? unavailableReason(doc) : null

  return (
    <article
      className={`doc-card${isActiveScope ? ' is-active' : ''}${isDeleting ? ' is-deleting' : ''}`}
      data-testid="document-card"
      data-document-id={doc.document_id}
    >
      <header className="doc-card-head">
        <div className="doc-card-icon" aria-hidden="true">
          <Icon name="doc" />
        </div>
        <div className="doc-card-titles">
          <h3 className="doc-card-title" title={title}>{title}</h3>
          {subtitleParts.length > 0 && (
            <div className="doc-card-subtitle">{subtitleParts.join(' · ')}</div>
          )}
        </div>
        <DocumentStatusBadge status={doc.status} />
        <button
          type="button"
          className="doc-card-delete"
          data-testid="document-delete"
          onClick={onRequestDelete}
          disabled={isDeleting}
          aria-label={`Excluir documento ${title}`}
          title={
            isActiveScope
              ? 'Excluir documento (limpa também o escopo ativo)'
              : 'Excluir documento'
          }
        >
          <Icon name="trash" />
        </button>
      </header>

      <dl className="doc-card-grid">
        <div>
          <dt>document_id</dt>
          <dd className="mono" title={doc.document_id}>{doc.document_id}</dd>
        </div>
        <div>
          <dt>Chunks</dt>
          <dd className={doc.chunk_count <= 0 ? 'is-warning' : ''}>
            {doc.chunk_count}
          </dd>
        </div>
        <div>
          <dt>Embedding</dt>
          <dd className="mono" title={doc.embedding_model ?? ''}>
            {doc.embedding_model ?? '—'}
          </dd>
        </div>
        <div>
          <dt>Indexado em</dt>
          <dd>{indexedAtFmt}</dd>
        </div>
        {showCreatedRow ? (
          <div>
            <dt>Criado em</dt>
            <dd>{createdAtFmt}</dd>
          </div>
        ) : null}
      </dl>

      {warning ? (
        <p className="doc-card-warning" role="status">
          <Icon name="close" />
          <span>{warning}</span>
        </p>
      ) : null}

      <footer className="doc-card-actions">
        <button
          type="button"
          className="doc-card-cta"
          data-testid="document-use-as-scope"
          disabled={!canUseAsScope || isDeleting}
          onClick={onUseAsScope}
          title={
            canUseAsScope
              ? 'Iniciar uma nova consulta com este documento no escopo'
              : warning ?? 'Documento ainda não está pronto para consulta'
          }
        >
          <Icon name="sparkles" />
          {isActiveScope ? 'Em uso no chat' : 'Usar no chat'}
        </button>
      </footer>
    </article>
  )
}
